import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { sendContactEmail } from "../../services/email_service";
import "./OpenLiftConversion.css";

const COPY = {
  fr: {
    brand: "OPENlift",
    stage: "PASSER À L'ACTION",
    eyebrow: "La prochaine étape est simple",
    titleStart: "Découvrez ce qu'OPENlift peut changer",
    titleEnd: "pour votre parc.",
    description:
      "Échangez avec notre équipe, voyez la plateforme en action et vérifiez les possibilités d'intégration à vos équipements.",
    primary: "Demander une démonstration",
    secondary: "Parler à un expert",
    reassurances: [
      "Démonstration adaptée à vos besoins",
      "Compatibilité étudiée selon votre parc",
      "Échange avec notre équipe"
    ],
    formEyebrow: "Parlons de votre parc",
    formTitle: "Demander une démonstration",
    formDescription:
      "Décrivez votre activité et vos besoins : notre équipe pourra préparer un échange adapté.",
    name: "Nom et prénom",
    company: "Société",
    email: "Adresse email professionnelle",
    country: "Pays",
    fleet: "Taille du parc (optionnel)",
    fleetOptions: ["1 à 20 ascenseurs", "21 à 100 ascenseurs", "101 à 500 ascenseurs", "Plus de 500 ascenseurs"],
    fleetPlaceholder: "Sélectionner si connu",
    message: "Votre message ou votre besoin",
    messagePlaceholder: "Par exemple : marques, contrôleurs, besoins de supervision...",
    honeypot: "Ne pas remplir ce champ",
    privacy:
      "Ces informations sont destinées à répondre à votre demande. Pour toute question relative à leur traitement, contactez-nous.",
    contactLink: "Nous contacter",
    submit: "Envoyer ma demande",
    sending: "Envoi en cours...",
    success: "Votre demande a été transmise. Merci, notre équipe pourra revenir vers vous.",
    error: "L'envoi n'a pas abouti. Réessayez ou utilisez la page Contact.",
    unconfigured:
      "L'envoi direct n'est pas encore configuré. Utilisez notre page Contact pour nous adresser votre demande.",
    unavailable: "Formulaire en attente de connexion au service d'envoi",
    backToContact: "Ouvrir la page Contact",
    required: "Champs obligatoires"
  },
  en: {
    brand: "OPENlift",
    stage: "NEXT STEP",
    eyebrow: "The next step is simple",
    titleStart: "Discover what OPENlift can change",
    titleEnd: "for your fleet.",
    description:
      "Talk to our team, see the platform in action, and explore the possible integrations with your equipment.",
    primary: "Request a demonstration",
    secondary: "Speak to an expert",
    reassurances: [
      "A demonstration tailored to your needs",
      "Compatibility reviewed for your fleet",
      "A conversation with our team"
    ],
    formEyebrow: "Let's talk about your fleet",
    formTitle: "Request a demonstration",
    formDescription:
      "Tell us about your business and needs so our team can prepare a relevant conversation.",
    name: "Full name",
    company: "Company",
    email: "Professional email address",
    country: "Country",
    fleet: "Fleet size (optional)",
    fleetOptions: ["1 to 20 elevators", "21 to 100 elevators", "101 to 500 elevators", "More than 500 elevators"],
    fleetPlaceholder: "Select if known",
    message: "Your message or requirements",
    messagePlaceholder: "For example: brands, controllers, monitoring requirements...",
    honeypot: "Leave this field empty",
    privacy:
      "This information is used to respond to your inquiry. Contact us with any questions about how it is handled.",
    contactLink: "Contact us",
    submit: "Send my request",
    sending: "Sending...",
    success: "Your request has been sent. Thank you; our team will be able to get back to you.",
    error: "We could not send your request. Please try again or use the Contact page.",
    unconfigured:
      "Direct submission is not yet configured. Please use our Contact page to send your request.",
    unavailable: "Form waiting for its delivery service to be connected",
    backToContact: "Open the Contact page",
    required: "Required fields"
  }
};

const EMPTY_FORM = {
  fullName: "",
  company: "",
  email: "",
  country: "",
  fleetSize: "",
  message: "",
  website: ""
};

function ArrowIcon({ diagonal = false }) {
  return diagonal ? (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none">
      <path d="M5 18 18 5M8 5h10v10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none">
      <path d="M4 12h15m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function OpenLiftConversion() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const text = COPY[language] || COPY.fr;

  const [form, setForm] = useState(EMPTY_FORM);
  const [state, setState] = useState("idle"); // idle | sending | success | error
  const requestInFlight = useRef(false);

  function changeField(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (state === "error") setState("idle");
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (state === "sending" || requestInFlight.current) return;

    const htmlForm = event.currentTarget;
    if (!htmlForm.checkValidity()) {
      htmlForm.reportValidity();
      return;
    }

    // Champ anti-robots de première ligne; une protection serveur reste indispensable.
    if (form.website.trim()) return;

    // Contrat historique du formulaire Contact : même sujet « demo », mêmes clés.
    // Le pays et la taille du parc sont intégrés au message pour ne rien perdre
    // et ne pas imposer de nouveaux champs au backend déjà fonctionnel.
    const fullName = form.fullName.trim().replace(/\s+/g, " ");
    const [firstname, ...surnameParts] = fullName.split(" ");
    const payload = {
      firstname,
      name: surnameParts.join(" ") || firstname,
      email: form.email.trim(),
      company: form.company.trim(),
      subject: "demo",
      message: [
        `OPENlift - ${text.formTitle}`,
        `${text.country} : ${form.country.trim()}`,
        ...(form.fleetSize ? [`${text.fleet} : ${form.fleetSize}`] : []),
        "",
        form.message.trim()
      ].join("\n")
    };

    requestInFlight.current = true;
    setState("sending");

    try {
      await sendContactEmail(payload);
      setState("success");
      setForm(EMPTY_FORM);
    } catch (error) {
      // Le diagnostic technique est déjà effectué par email_service.js.
      setState("error");
    } finally {
      requestInFlight.current = false;
    }
  }

  const feedback = state === "success"
    ? text.success
    : state === "error"
      ? text.error
      : "";

  return (
    <section className="ol-conversion" id="ol-conversion" aria-labelledby="ol-conversion-title">
      <div className="ol-conversion__container">
        <div className="ol-conversion__topline" aria-hidden="true">
          <span>{text.brand}</span>
          <span>{text.stage}</span>
        </div>

        <div className="ol-conversion__layout">
          <div className="ol-conversion__copy">
            <p className="ol-conversion__eyebrow"><span aria-hidden="true" />{text.eyebrow}</p>
            <h2 className="ol-conversion__title" id="ol-conversion-title">
              {text.titleStart} <span>{text.titleEnd}</span>
            </h2>
            <p className="ol-conversion__intro">{text.description}</p>

            <div className="ol-conversion__actions">
              <a className="ol-conversion__button ol-conversion__button--primary" href="#ol-conversion-form">
                {text.primary}<ArrowIcon />
              </a>
              <Link className="ol-conversion__button ol-conversion__button--outline" to="/contact">
                {text.secondary}<ArrowIcon />
              </Link>
            </div>

            <ul className="ol-conversion__reassurances">
              {text.reassurances.map((item) => (
                <li key={item}>
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
                    <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="ol-conversion__formColumn">
            <form
              className="ol-conversion__form"
              id="ol-conversion-form"
              onSubmit={handleSubmit}
              aria-label={text.formTitle}
              noValidate={false}
            >
              <p className="ol-conversion__formEyebrow">{text.formEyebrow}</p>
              <h3 className="ol-conversion__formTitle">{text.formTitle}</h3>
              <p className="ol-conversion__formDescription">{text.formDescription}</p>

              <div className="ol-conversion__fields">
                <label className="ol-conversion__field">
                  <span>{text.name} <span aria-hidden="true">*</span></span>
                  <input name="fullName" value={form.fullName} onChange={changeField}
                    autoComplete="name" maxLength={120} required />
                </label>
                <label className="ol-conversion__field">
                  <span>{text.company} <span aria-hidden="true">*</span></span>
                  <input name="company" value={form.company} onChange={changeField}
                    autoComplete="organization" maxLength={140} required />
                </label>
                <label className="ol-conversion__field">
                  <span>{text.email} <span aria-hidden="true">*</span></span>
                  <input name="email" type="email" value={form.email} onChange={changeField}
                    autoComplete="email" maxLength={180} required />
                </label>
                <label className="ol-conversion__field">
                  <span>{text.country} <span aria-hidden="true">*</span></span>
                  <input name="country" value={form.country} onChange={changeField}
                    autoComplete="country-name" maxLength={80} required />
                </label>
                <label className="ol-conversion__field ol-conversion__field--wide">
                  <span>{text.fleet}</span>
                  <select name="fleetSize" value={form.fleetSize} onChange={changeField}>
                    <option value="">{text.fleetPlaceholder}</option>
                    {text.fleetOptions.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label className="ol-conversion__field ol-conversion__field--wide">
                  <span>{text.message} <span aria-hidden="true">*</span></span>
                  <textarea name="message" value={form.message} onChange={changeField}
                    placeholder={text.messagePlaceholder} rows={3} maxLength={2000} required />
                </label>
              </div>

              <div className="ol-conversion__honeypot" aria-hidden="true">
                <label htmlFor="ol-conversion-website">{text.honeypot}</label>
                <input id="ol-conversion-website" name="website" type="text" tabIndex={-1}
                  autoComplete="off" value={form.website} onChange={changeField} />
              </div>

              <button type="submit" className="ol-conversion__submit" disabled={state === "sending"}>
                {state === "sending" ? text.sending : text.submit}
                <ArrowIcon />
              </button>

              <p className="ol-conversion__privacy">
                {text.privacy} <Link to="/contact">{text.contactLink}</Link>.
              </p>

              {feedback && (
                <div className={`ol-conversion__feedback ol-conversion__feedback--${state}`}
                  role={state === "success" ? "status" : "alert"}>
                  <p>{feedback}</p>
                  {state !== "success" && (
                    <Link to="/contact">{text.backToContact}<ArrowIcon diagonal /></Link>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
